import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { find, isNil, map } from "lodash";
import { useMemo, useState } from "react";
import { CabinetState } from "../../../models/DeviceHistory";
import { CabinetStateSelectorProps, CabinetStateWithDisplayDto } from "./types";

const cabinetStatesWithDisplayName: CabinetStateWithDisplayDto[] = [
	{
		state: CabinetState.CLOSED,
		displayName: "Cerrado",
	},
	{
		state: CabinetState.OPEN,
		displayName: "Abierto",
	},
];

export default function CabinetStateSelector(props: CabinetStateSelectorProps) {
	const { reactOnChange } = props;

	const [selectedCabinetState, setSelectedCabinetState] = useState<
		CabinetStateWithDisplayDto | { state: string }
	>({
		state: "all",
	});

	const options = useMemo(() => cabinetStatesWithDisplayName, []);

	return (
		<Box sx={{ width: "100%" }}>
			<FormControl
				fullWidth
				size='small'
				variant='outlined'
				sx={{
					"& .MuiInputLabel-root": {
						color: "rgba(15, 23, 42, 0.62)",
						fontSize: 12,
						fontWeight: 700,
					},
					"& .MuiOutlinedInput-root": {
						borderRadius: 2,
						backgroundColor: "rgba(248, 250, 252, 0.96)",
						"& fieldset": { borderColor: "rgba(15, 23, 42, 0.14)" },
						"&:hover fieldset": { borderColor: "rgba(2, 132, 199, 0.45)" },
						"&.Mui-focused fieldset": {
							borderColor: "#0284c7",
							borderWidth: 1,
						},
					},
					"& .MuiSelect-select": {
						display: "flex",
						alignItems: "center",
						gap: 1,
						py: 1.25,
						fontWeight: 700,
						color: "#172033",
					},
				}}>
				<InputLabel id='cabinet-state-select-label'>Gabinete</InputLabel>
				<Select
					labelId='cabinet-state-select-label'
					id='cabinet-state-select'
					value={selectedCabinetState ? selectedCabinetState.state : "all"}
					label='Gabinete'
					defaultValue=''
					renderValue={(selected) => {
						const option = find(
							options,
							(cabinetStateWithDisplay: CabinetStateWithDisplayDto) =>
								cabinetStateWithDisplay.state === selected,
						);
						return (
							<Box
								component='span'
								sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{selected === CabinetState.CLOSED ? (
									<CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
								) : selected === CabinetState.OPEN ? (
									<ErrorIcon sx={{ color: "#dc2626", fontSize: 18 }} />
								) : (
									<Inventory2Icon sx={{ color: "#0284c7", fontSize: 18 }} />
								)}
								{option?.displayName || "Todos los Status"}
							</Box>
						);
					}}
					MenuProps={{
						PaperProps: {
							sx: {
								mt: 0.75,
								borderRadius: 2,
								boxShadow: "0 18px 44px rgba(15, 23, 42, 0.16)",
								"& .MuiMenuItem-root": {
									mx: 0.75,
									my: 0.35,
									borderRadius: 1.5,
									fontWeight: 600,
								},
							},
						},
					}}
					onChange={(e: SelectChangeEvent) => {
						let cabinetStateMatch = find(
							options,
							(cabinetStateWithDisplay: CabinetStateWithDisplayDto) =>
								cabinetStateWithDisplay.state === e.target.value,
						);

						if (isNil(cabinetStateMatch)) {
							cabinetStateMatch = {
								state: "all",
								displayName: "Todos los Status",
							};
						}

						setSelectedCabinetState(cabinetStateMatch);
						reactOnChange(cabinetStateMatch);
					}}>
					<MenuItem value={"all"}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Inventory2Icon sx={{ color: "#0284c7", fontSize: 18 }} />
							<Typography variant='body2' sx={{ fontWeight: 700 }}>
								Todos los Status
							</Typography>
						</Box>
					</MenuItem>
					{map(
						options,
						(cabinetStateWithDisplay: CabinetStateWithDisplayDto) => {
							return (
								<MenuItem
									key={cabinetStateWithDisplay.state}
									value={cabinetStateWithDisplay.state}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										{cabinetStateWithDisplay.state === CabinetState.CLOSED ? (
											<CheckCircleIcon
												sx={{ color: "#16a34a", fontSize: 18 }}
											/>
										) : (
											<ErrorIcon sx={{ color: "#dc2626", fontSize: 18 }} />
										)}
										<Typography variant='body2' sx={{ fontWeight: 700 }}>
											{cabinetStateWithDisplay.displayName}
										</Typography>
									</Box>
								</MenuItem>
							);
						},
					)}
				</Select>
			</FormControl>
		</Box>
	);
}
